import { Stack } from '@fluentui/react';
import {
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
} from '@fluentui/react-components';
import { useFetch } from '@moko/ui';
import type { IMetadata, IOrderOptions } from '../../services/metadataService';
import {
  getPricingItems,
  type IPricingItem,
} from '../../services/pricingService';

export const ADDITIONAL_ASSET_PRICING_HINT =
  'Automatically charge for linked assets beyond the included quantity. Configure the rate in Pricing rates and the included quantity using the includedUnits pricing parameter. Leave empty when no additional asset charge applies.';

export const isAdditionalAssetPricingItem = (item: IPricingItem) =>
  item.isDeleted !== true &&
  item.quantityType === 'AssetCount' &&
  item.calculationMethod === 'UnitRate' &&
  item.chargeRole === 'AdditionalCharge';

const sameCode = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();

const pricingItemLabel = (item: IPricingItem) => `${item.name} (${item.code})`;

const additionalAssetValidation = (
  code: string | null | undefined,
  items: IPricingItem[] | null,
  loadError: Error | null
) => {
  if (loadError) return 'Pricing items could not be loaded.';
  if (!code || !items) return undefined;
  const matches = items.filter((item) => sameCode(item.code, code));
  if (matches.length === 0 || matches.every((item) => item.isDeleted)) {
    return `Pricing item "${code}" is unavailable. Select an eligible item or clear this setting.`;
  }
  if (!matches.some(isAdditionalAssetPricingItem)) {
    return `Pricing item "${code}" is no longer eligible. It must be an asset count, unit rate, additional charge item. Select an eligible item or clear this setting.`;
  }
  return undefined;
};

interface Props {
  metadata?: IMetadata;
  setMetadata: (metadata: IMetadata) => void;
  setMetadataStr: (value: string) => void;
}

export default function OrderOptions({
  metadata,
  setMetadata,
  setMetadataStr,
}: Props) {
  const options = metadata?.orderOptions ?? {};
  const proximity = options.proximity;
  const pricingItemsFetch = useFetch<IPricingItem[]>({
    queryKey: ['pricingItems'],
    queryFn: async (abortController) =>
      [...(await getPricingItems(abortController, 500, 1)).items].sort(
        (a, b) => a.sortOrder - b.sortOrder
      ),
  });
  const eligibleItems = (pricingItemsFetch.data ?? []).filter(
    isAdditionalAssetPricingItem
  );
  const additionalAssetCode = options.additionalAssetPricingItemCode;
  const selectedAdditionalAssetItem = additionalAssetCode
    ? eligibleItems.find((item) => sameCode(item.code, additionalAssetCode))
    : undefined;
  const additionalAssetError = additionalAssetValidation(
    additionalAssetCode,
    pricingItemsFetch.data,
    pricingItemsFetch.error
  );
  const update = (change: Partial<IOrderOptions>) => {
    const next: IMetadata = {
      ...metadata,
      fields: metadata?.fields ?? [],
      lookups: metadata?.lookups ?? [],
      validations: metadata?.validations ?? [],
      interactions: metadata?.interactions ?? [],
      orderOptions: { ...options, ...change },
    };
    setMetadata(next);
    setMetadataStr(JSON.stringify(next, null, 2));
  };
  const updateProximity = (
    change: Partial<NonNullable<IOrderOptions['proximity']>>
  ) => update({ proximity: { enabled: false, ...proximity, ...change } });

  return (
    <Stack tokens={{ childrenGap: 20, padding: 20 }}>
      <Text>
        These settings apply to assets linked to Orders using this form. Leave
        the limit blank and proximity disabled for unrestricted equipment
        selection.
      </Text>
      <Text>
        These settings can be saved now. Order-time enforcement is not enabled
        yet.
      </Text>
      <Field
        label="Maximum linked assets"
        hint="For site-based forms, each linked asset represents one inspection site. Blank means no limit."
        validationMessage={
          options.maximumAssetCount != null &&
          (!Number.isInteger(options.maximumAssetCount) ||
            options.maximumAssetCount < 1)
            ? 'Enter a positive whole number.'
            : undefined
        }
      >
        <Input
          type="number"
          min={1}
          step={1}
          value={options.maximumAssetCount?.toString() ?? ''}
          onChange={(_, data) =>
            update({
              maximumAssetCount: data.value === '' ? null : Number(data.value),
            })
          }
        />
      </Field>
      <Field
        label="Additional asset pricing item"
        hint={ADDITIONAL_ASSET_PRICING_HINT}
        validationMessage={additionalAssetError}
      >
        <Dropdown
          clearable
          placeholder={
            pricingItemsFetch.loading
              ? 'Loading pricing items...'
              : 'No additional asset charge'
          }
          value={
            selectedAdditionalAssetItem
              ? pricingItemLabel(selectedAdditionalAssetItem)
              : additionalAssetCode ?? ''
          }
          selectedOptions={
            selectedAdditionalAssetItem
              ? [selectedAdditionalAssetItem.code]
              : additionalAssetCode
              ? [additionalAssetCode]
              : []
          }
          onOptionSelect={(_, data) =>
            update({ additionalAssetPricingItemCode: data.optionValue ?? null })
          }
        >
          {eligibleItems.map((item) => (
            <Option key={item.id} value={item.code}>
              {pricingItemLabel(item)}
            </Option>
          ))}
          {additionalAssetCode && !selectedAdditionalAssetItem && (
            <Option value={additionalAssetCode} disabled>
              {`${additionalAssetCode} (unavailable)`}
            </Option>
          )}
        </Dropdown>
      </Field>
      <Checkbox
        label="Check proximity between linked assets"
        checked={proximity?.enabled ?? false}
        onChange={(_, data) =>
          updateProximity({ enabled: data.checked === true })
        }
      />
      {proximity?.enabled && (
        <>
          <Field
            label="Maximum distance (miles)"
            required
            validationMessage={
              proximity.maximumDistanceMiles != null &&
              proximity.maximumDistanceMiles <= 0
                ? 'Enter a distance greater than zero.'
                : undefined
            }
          >
            <Input
              type="number"
              min={0}
              step="any"
              value={proximity.maximumDistanceMiles?.toString() ?? ''}
              onChange={(_, data) =>
                updateProximity({
                  maximumDistanceMiles:
                    data.value === '' ? null : Number(data.value),
                })
              }
            />
          </Field>
          <Field label="Distance measurement" required>
            <Dropdown
              placeholder="Select measurement"
              value={
                proximity.measurement === 'StraightLine'
                  ? 'Straight-line distance'
                  : proximity.measurement === 'Driving'
                  ? 'Driving distance'
                  : ''
              }
              selectedOptions={
                proximity.measurement ? [proximity.measurement] : []
              }
              onOptionSelect={(_, data) =>
                updateProximity({
                  measurement: data.optionValue as 'StraightLine' | 'Driving',
                })
              }
            >
              <Option value="StraightLine">Straight-line distance</Option>
              <Option value="Driving">Driving distance</Option>
            </Dropdown>
          </Field>
          <Field label="Compare locations" required>
            <Dropdown
              placeholder="Select comparison"
              value={
                proximity.comparison === 'AllPairs'
                  ? 'Every pair of sites'
                  : proximity.comparison === 'FromPrimary'
                  ? 'Primary site to each additional site'
                  : ''
              }
              selectedOptions={
                proximity.comparison ? [proximity.comparison] : []
              }
              onOptionSelect={(_, data) =>
                updateProximity({
                  comparison: data.optionValue as 'AllPairs' | 'FromPrimary',
                })
              }
            >
              <Option value="AllPairs">Every pair of sites</Option>
              <Option value="FromPrimary">
                Primary site to each additional site
              </Option>
            </Dropdown>
          </Field>
          <Text>
            Choose the agreed business rule before publishing. Saving these
            settings does not yet enable distance enforcement; Order assessment
            integration is pending.
          </Text>
        </>
      )}
    </Stack>
  );
}
